import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { baseUrl } from "../../config/baseUrl";

const formatDate = (value) => {
  if (!value) {
    return "-";
  }

  return new Date(value).toLocaleDateString();
};

const isCouponExpired = (validTill) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const expiryDate = new Date(validTill);
  expiryDate.setHours(0, 0, 0, 0);

  return today > expiryDate;
};

export default function OwnerCouponList() {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(null);
  const [statusLoading, setStatusLoading] = useState(null);

  useEffect(() => {
    const fetchCoupons = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${baseUrl}/owner/coupons`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });

        setCoupons(response.data.data || []);
      } catch (error) {
        console.error("Fetch coupons error:", error);
        toast.error("Failed to fetch coupons");
      } finally {
        setLoading(false);
      }
    };

    fetchCoupons();
  }, []);

  const handleDelete = async (couponId) => {
    if (!window.confirm("Are you sure you want to delete this coupon?")) {
      return;
    }

    try {
      setDeleteLoading(couponId);
      const response = await axios.delete(`${baseUrl}/owner/coupons/${couponId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (response.data.status) {
        toast.success("Coupon deleted successfully");
        setCoupons((prev) => prev.filter((item) => item._id !== couponId));
      }
    } catch (error) {
      console.error("Delete coupon error:", error);
      toast.error(error.response?.data?.message || "Failed to delete coupon");
    } finally {
      setDeleteLoading(null);
    }
  };

  const handleStatusToggle = async (couponId, currentStatus) => {
    try {
      setStatusLoading(couponId);
      const response = await axios.patch(
        `${baseUrl}/owner/coupons/status/${couponId}`,
        { isActive: !currentStatus },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.data.status) {
        toast.success("Coupon status updated successfully");
        setCoupons((prev) =>
          prev.map((item) =>
            item._id === couponId ? { ...item, isActive: !currentStatus } : item
          )
        );
      }
    } catch (error) {
      console.error("Coupon status error:", error);
      toast.error(error.response?.data?.message || "Failed to update status");
    } finally {
      setStatusLoading(null);
    }
  };

  if (loading) {
    return (
      <section className="main-sec">
        <div className="text-center py-5">
          <div className="spinner-border" role="status" />
          <p className="mt-2">Loading coupons...</p>
        </div>
      </section>
    );
  }

  return (
    <section className="main-sec">
      <div className="row align-items-center">
        <div className="col-lg-8">
          <div className="dashboard-title">
            <h4 className="dash-head">
              <i className="fa fa-ticket me-2" />
              Coupons
            </h4>
          </div>
          <div className="custom-bredcump">
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to="/">Dashboard</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Coupons
                </li>
              </ol>
            </nav>
          </div>
        </div>
        <div className="col-lg-4 d-flex justify-content-lg-end">
          <Link to="add" className="btn-add-question mt-2 mt-lg-0">
            <i className="fa fa-plus me-2" />
            Add Coupon
          </Link>
        </div>

        <div className="col-lg-12">
          <div className="cards bus-list">
            <div className="bus-filter">
              <div className="row align-items-center">
                <div className="col-lg-6">
                  <h5 className="card-title mb-1">Coupon List ({coupons.length})</h5>
                  <small className="text-muted">
                    These coupons are limited to your library only.
                  </small>
                </div>
              </div>
            </div>

            <div className="table-responsive custom-table">
              <table className="table table-borderless">
                <thead>
                  <tr>
                    <th>SN</th>
                    <th>Coupon Code</th>
                    <th>Discount</th>
                    <th>Valid From</th>
                    <th>Valid Till</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {coupons.length > 0 ? (
                    coupons.map((coupon, index) => {
                      const expired = isCouponExpired(coupon.validTill);
                      const effectiveStatus = !expired && coupon.isActive;

                      return (
                        <tr key={coupon._id}>
                          <td>{index + 1}</td>
                          <td>
                            <span className="coupon-code-badge">{coupon.code}</span>
                          </td>
                          <td>
                            {coupon.discountType === "fixed"
                              ? `Rs ${coupon.discountValue}`
                              : `${coupon.discountValue}%`}
                          </td>
                          <td>{formatDate(coupon.validFrom)}</td>
                          <td>
                            {formatDate(coupon.validTill)}
                            {expired && (
                              <span className="badge bg-danger ms-2">Expired</span>
                            )}
                          </td>
                          <td>
                            <button
                              type="button"
                              className={`btn btn-sm ${
                                effectiveStatus ? "btn-success" : "btn-danger"
                              }`}
                              onClick={() =>
                                handleStatusToggle(coupon._id, coupon.isActive)
                              }
                              disabled={statusLoading === coupon._id || expired}
                            >
                              {statusLoading === coupon._id ? (
                                <span className="spinner-border spinner-border-sm me-1" />
                              ) : null}
                              {effectiveStatus ? "Active" : "Inactive"}
                            </button>
                          </td>
                          <td>
                            <div className="d-flex gap-2">
                              <Link
                                to={`edit/${coupon._id}`}
                                className="btn btn-outline-primary btn-sm"
                                title="Edit"
                              >
                                <i className="fa fa-edit" />
                              </Link>
                              <button
                                type="button"
                                className="btn btn-outline-danger btn-sm"
                                title="Delete"
                                onClick={() => handleDelete(coupon._id)}
                                disabled={deleteLoading === coupon._id}
                              >
                                {deleteLoading === coupon._id ? (
                                  <span className="spinner-border spinner-border-sm" />
                                ) : (
                                  <i className="fa fa-trash" />
                                )}
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="7" className="text-center py-5">
                        <div className="text-muted">
                          <i className="fa fa-ticket fa-2x mb-3 d-block" />
                          <p className="mb-3">No coupons found for your library.</p>
                          <Link to="add" className="btn btn-primary btn-sm">
                            <i className="fa fa-plus me-1" />
                            Create First Coupon
                          </Link>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <style>
        {`
          .coupon-code-badge {
            display: inline-block;
            padding: 6px 12px;
            border: 2px dashed #f5c6cb;
            border-radius: 6px;
            color: #d9534f;
            background-color: #fcebea;
            font-weight: 700;
            letter-spacing: 1px;
          }
        `}
      </style>
    </section>
  );
}
